import type { LinkState, LinkRendererContext, PromotedLinkId } from './diagram.types';
import { classNames } from '#utils/UNSAFE_classNames';
import { itemStyles } from './themes/DiagramStyles.css';
import { ComponentChildren } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import { useResizeObserver } from '#hooks/UNSAFE_useResizeObserver';
import { getItemAriaLabel, getPromotedString } from './utils/diagramUtils';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';

/**
 * Props for Link
 */
export type LinkProps<K1, D2, L> = {
  id: PromotedLinkId<K1, D2>;
  state: LinkState;
  isDimmed?: boolean;
  activeId?: string;
  linkRenderer: (context: LinkRendererContext<K1, D2, L>) => ComponentChildren;
  supportsSelection: boolean;
  layoutOutput: L;
  onLinkSizeChanged: (width: number, height: number, id: string) => void;
};

export const DiagramPromotedLinkElement = <K1, D2, L>({
  id,
  state,
  activeId,
  linkRenderer,
  isDimmed,
  supportsSelection,
  layoutOutput,
  onLinkSizeChanged
}: LinkProps<K1, D2, L>) => {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { dimmedItemStyle, linkStyles } = itemStyles;
  const context = {
    state: state,
    data: id.links,
    layoutOutput: layoutOutput,
    startNode: id.startNode,
    endNode: id.endNode,
    type: 'promotedLink'
  } as LinkRendererContext<K1, D2, L>;
  const ref = useRef<SVGGElement>(null);
  const optionsRef = useRef({ box: 'border-box' as const });
  const stringId = getPromotedString(id);
  useResizeObserver(
    ref,
    useCallback(
      (entry: ResizeObserverEntry) => {
        const boxSize = entry.borderBoxSize;
        const width = boxSize[0].inlineSize;
        const height = boxSize[0].blockSize;
        onLinkSizeChanged(width, height, stringId);
      },
      [stringId, onLinkSizeChanged]
    ),
    optionsRef.current
  );
  const num_links = id.links.length;
  const accessibleLabel =
    num_links > 1
      ? translations.diagram_promotedLinks({ num_links: num_links.toString() })
      : translations.diagram_promotedLink();
  return (
    <g
      ref={ref}
      key={stringId}
      id={state.isFocused ? activeId : undefined}
      aria-label={getItemAriaLabel(
        translations,
        supportsSelection,
        {
          isSelected: state.isSelected,
          isIndirect: true
        },
        accessibleLabel
      )}
      data-oj-private-promoted-link={stringId}
      role="img"
      class={classNames([linkStyles, isDimmed && dimmedItemStyle])}>
      {linkRenderer(context)}
    </g>
  );
};
